/**
 * QuickActionsPanel Component
 * Provides quick access buttons for common actions like uploading invoices and manual entry
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from '../ui/Button';
import { Icon, IconName } from '../ui/Icon';
import { ClientManagementModal } from './ClientManagementModal';

interface QuickActionsPanelProps {
  onNavigate?: (path: string) => void;
  className?: string;
}

const Panel = styled.div`
  min-width: 0;
  padding: 1.25rem;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
`;

const Header = styled.div`
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-muted);
`;

const ActionsGrid = styled.div`
  display: grid;
  gap: 0.75rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ActionCard = styled.div`
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border-light);
  &:last-child {
    border-bottom: 0;
  }
`;

const ActionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

const ActionIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ActionTitle = styled.h4`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const ActionDescription = styled.p`
  margin: 0 0 0.75rem 0;
  font-size: 0.75rem;
  color: var(--text-muted);
  line-height: 1.4;
`;

const actions = [
  {
    id: 'upload',
    iconName: 'file-upload' as IconName,
    title: 'Subir factura',
    description: 'Sube una factura y revisa los datos extraídos.',
    path: '/upload',
    primary: true,
    type: 'navigate' as const,
  },
  {
    id: 'manual',
    iconName: 'edit' as IconName,
    title: 'Entrada manual',
    description:
      'Introduce manualmente los datos de consumo de electricidad, agua o gas',
    path: '/manual',
    primary: false,
    type: 'navigate' as const,
  },
  {
    id: 'clients',
    iconName: 'users' as IconName,
    title: 'Registrar cubiertos',
    description:
      'Registra el número de clientes y cubiertos servidos para calcular métricas por persona',
    primary: false,
    type: 'modal' as const,
  },
  {
    id: 'invoices',
    iconName: 'clipboard' as IconName,
    title: 'Actividad',
    description: 'Revisa y edita todas tus facturas procesadas',
    path: '/invoices',
    primary: false,
    type: 'navigate' as const,
  },
];

export const QuickActionsPanel: React.FC<QuickActionsPanelProps> = ({
  onNavigate,
  className,
}) => {
  const navigate = useNavigate();
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);

  const handleAction = (action: (typeof actions)[0]) => {
    if (action.type === 'modal') {
      if (action.id === 'clients') {
        setIsClientModalOpen(true);
      }
    } else if (action.type === 'navigate' && action.path) {
      if (onNavigate) {
        onNavigate(action.path);
      } else {
        navigate(action.path);
      }
    }
  };

  const handleClientModalClose = () => {
    setIsClientModalOpen(false);
  };

  const handleClientModalSuccess = () => {
    // Could trigger dashboard refresh here if needed
    console.log('✅ Client data saved successfully');
  };

  return (
    <Panel className={className}>
      <Header>
        <Title>Acciones Rápidas</Title>
        <Subtitle>Gestión de consumos</Subtitle>
      </Header>

      <ActionsGrid>
        {actions.map((action) => (
          <ActionCard key={action.id}>
            <ActionHeader>
              <ActionIconContainer>
                <Icon
                  name={action.iconName}
                  size="lg"
                  aria-label={`${action.title} icon`}
                />
              </ActionIconContainer>
              <ActionTitle>{action.title}</ActionTitle>
            </ActionHeader>

            <ActionDescription>{action.description}</ActionDescription>

            <Button
              variant={action.primary ? 'primary' : 'secondary'}
              size="sm"
              fullWidth
              onClick={() => handleAction(action)}
              aria-label={
                action.type === 'modal'
                  ? `Abrir ${action.title}`
                  : `Ir a ${action.title}`
              }
            >
              {action.primary
                ? 'Comenzar'
                : action.type === 'modal'
                  ? 'Registrar'
                  : 'Ver'}
            </Button>
          </ActionCard>
        ))}
      </ActionsGrid>

      {/* Client Management Modal */}
      <ClientManagementModal
        isOpen={isClientModalOpen}
        onClose={handleClientModalClose}
        onSuccess={handleClientModalSuccess}
      />
    </Panel>
  );
};

export default QuickActionsPanel;
